import moment from "moment";
import momentTz from "moment-timezone";
import {
  FETCH_DEVICE_REQUEST,
  FETCH_DEVICE_SUCCESS,
  FETCH_DEVICE_ERROR,
  FETCH_DEVICE_PROPERTY_REQUEST,
  FETCH_DEVICE_PROPERTY_SUCCESS,
  FETCH_DEVICE_PROPERTY_ERROR,
  GET_DEVICE_DETAIL_REQUEST,
  GET_DEVICE_DETAIL_SUCCESS,
  GET_DEVICE_DETAIL_ERROR,
  FETCH_DEVICE_PROPERTY_SET_REQUEST,
  FETCH_DEVICE_PROPERTY_SET_SUCCESS,
  FETCH_DEVICE_PROPERTY_SET_ERROR,
  ADD_DEVICE_REQUEST,
  ADD_DEVICE_SUCCESS,
  ADD_DEVICE_ERROR,
  DELETE_DEVICE_REQUEST,
  DELETE_DEVICE_ERROR,
  GET_DEVICE_DETAIL_PROPERTY_ERROR,
  UPDATE_DEVICE_REQUEST,
  UPDATE_DEVICE_SUCCESS,
  UPDATE_DEVICE_ERROR,
  CREATE_PROPERTY_SET_REQUEST,
  CREATE_PROPERTY_SET_SUCCESS,
  CREATE_PROPERTY_SET_ERROR,
  DELETE_PROPERTY_SET_REQUEST,
  DELETE_PROPERTY_SET_ERROR,
  RESET_SUCCESS,
  FETCH_PROPERTY_SET_DETAIL_REQUEST,
  FETCH_PROPERTY_SET_DETAIL_SUCCESS,
  FETCH_PROPERTY_SET_DETAIL_ERROR,
  UPDATE_PROPERTY_SET_REQUEST,
  UPDATE_PROPERTY_SET_SUCCESS,
  UPDATE_PROPERTY_SET_ERROR,
  GET_DEVICE_PROPERTY_REQUEST,
  GET_DEVICE_PROPERTY_SUCCESS,
  GET_DEVICE_PROPERTY_ERROR,
  UPDATE_DEVICE_PROPERTY_REQUEST,
  UPDATE_DEVICE_PROPERTY_SUCCESS,
  UPDATE_DEVICE_PROPERTY_ERROR,
  CREATE_DEVICE_PROPERTY_REQUEST,
  CREATE_DEVICE_PROPERTY_SUCCESS,
  CREATE_DEVICE_PROPERTY_ERROR,
  DELETE_PROPERTY_REQUEST,
  DELETE_PROPERTY_ERROR,
  FETCH_DEVICE_FIRMWARE_REQUEST,
  FETCH_DEVICE_FIRMWARE_SUCCESS,
  FETCH_DEVICE_FIRMWARE_ERROR,
  FETCH_FIRMWARE_DETAIL_REQUEST,
  FETCH_FIRMWARE_DETAIL_SUCCESS,
  FETCH_FIRMWARE_DETAIL_ERROR,
  UPDATE_FIRMWARE_DETAIL_REQUEST,
  UPDATE_FIRMWARE_DETAIL_SUCCESS,
  UPDATE_FIRMWARE_DETAIL_ERROR,
  CREATE_FIRMWARE_DETAIL_REQUEST,
  CREATE_FIRMWARE_DETAIL_SUCCESS,
  CREATE_FIRMWARE_DETAIL_ERROR,
  DUPLICATE_DEVICE_REQUEST,
  DUPLICATE_DEVICE_SUCCESS,
  DUPLICATE_DEVICE_ERROR,
  RESET_GRAPH_SUCCESS,
  FETCH_DEVICE_SPECIFIED_REQUEST,
  FETCH_DEVICE_SPECIFIED_SUCCESS,
  FETCH_DEVICE_SPECIFIED_ERROR,
  GET_PROPERTY_DATA_REQUEST,
  GET_PROPERTY_DATA_SUCCESS,
  FETCH_STAT_SAMPLES_REQUEST,
  FETCH_STAT_SAMPLES_SUCCESS,
  FETCH_STAT_SAMPLES_ERROR,
  CREATE_DEVICE_DATA_REQUEST,
  CREATE_DEVICE_DATA_SUCCESS,
  CREATE_DEVICE_DATA_ERROR,
  UPDATE_DEVICE_DATA_REQUEST,
  UPDATE_DEVICE_DATA_ERROR,
  UPDATE_DEFAULT_DATA_REQUEST,
  UPDATE_DEFAULT_DATA_ERROR,
  UPDATE_DEFAULT_THRESHOLD_REQUEST,
  UPDATE_DEFAULT_THRESHOLD_ERROR,
  DELETE_DEVICE_DATA_REQUEST,
  DELETE_DEVICE_DATA_ERROR,
  SYNC_FRIENDLY_NAME_REQUEST,
  SYNC_FRIENDLY_NAME_ERROR,
  FETCH_FRIENDLY_NAME_REQUEST,
  FETCH_FRIENDLY_NAME_SUCCESS,
  FETCH_FRIENDLY_NAME_ERROR,
  DOWNLOAD_DEVICE_FIRMWARE_REQUEST,
  DOWNLOAD_DEVICE_FIRMWARE_SUCCESS,
  DOWNLOAD_DEVICE_FIRMWARE_ERROR,
  FETCH_ALERT_LOG_DATA_REQUEST,
  FETCH_ALERT_LOG_DATA_SUCCESS,
  FETCH_ALERT_LOG_DATA_ERROR,
  FETCH_DIAGNOSTIC_PANEL_DATA_SUCCESS,
  FETCH_DIAGNOSTIC_PANEL_DATA_ERROR,
  FETCH_DIAGNOSTIC_PANEL_DATA_REQUEST,
  RESET_DIAGNOSTIC_PANEL_DATA_REQUEST,
  REMOVE_DIAGNOSTIC_USER_SLOT,
  SAVE_DIAGNOSTIC_PANEL_DATA_SUCCESS,
  SAVE_DIAGNOSTIC_PANEL_DATA_ERROR,
  SAVE_DIAGNOSTIC_PANEL_DATA_REQUEST,
  FETCH_SAVED_DIAGNOSTIC_PANEL_DATA_REQUEST,
  FETCH_SAVED_DIAGNOSTIC_PANEL_DATA_SUCCESS,
  FETCH_SAVED_DIAGNOSTIC_PANEL_DATA_ERROR,
  FETCH_DIAGNOSTIC_PANEL_REQUEST,
  FETCH_DIAGNOSTIC_PANEL_SUCCESS,
  FETCH_DIAGNOSTIC_PANEL_ERROR,
  SAVE_USER_ORDER_REQUEST,
  SAVE_USER_ORDER_ERROR,
  SAVE_USER_ORDER_SUCCESS,
  FETCH_ERROR_LOG_CSV_SUCCESS,
  FETCH_ERROR_LOG_CSV_ERROR,
  FETCH_ALERT_LOG_CSV_REQUEST,
  FETCH_ALERT_LOG_CSV_SUCCESS,
  FETCH_ALERT_LOG_CSV_ERROR
} from "./actions";
import { REDUCERS, DEFAULT_UTC_TIME } from "../../common/constants";
import { createReducer } from "../../common/helpers";
import timezones from "../../common/timezones.json";

var d = new Date();

// Panels render in Object.keys order, so keep the table map alphabetical no
// matter which panel's response lands first.
const sortDeviceTable = (tables) =>
  Object.keys(tables)
    .sort()
    .reduce((acc, key) => ({ ...acc, [key]: tables[key] }), {});

const emptyDiagnosticChart = () => [
  ["Time", "First User"],
  [new Date(), 0],
];

// Merges each user's series into Google Chart rows: [time, user1Value, user2Value, ...].
// Timestamps from different devices can drift by a second or two, so points within
// +/-2 seconds are treated as the same moment. Rows where a user has no point are skipped.
const buildDiagnosticRows = (seriesList, timeZone) => {
  const baseIndex = seriesList.findIndex((series) => series.length > 0);
  if (baseIndex === -1) return [];

  const lookups = seriesList.map((series) =>
    series.reduce((acc, item) => {
      acc.set(moment(item.view.groupDate).unix(), item.view.value);
      return acc;
    }, new Map())
  );

  const findValue = (lookup, unixTime) => {
    for (const offset of [0, 1, 2, -1, -2]) {
      if (lookup.has(unixTime + offset)) return { found: true, value: lookup.get(unixTime + offset) };
    }
    return { found: false };
  };

  return seriesList[baseIndex].reduce((rows, point) => {
    const unixTime = moment(point.view.groupDate).unix();
    const values = [];
    for (let index = 0; index < seriesList.length; index++) {
      // A user with no data at all is drawn as a flat 0 line instead of hiding the chart
      if (index === baseIndex || seriesList[index].length === 0) {
        values.push(index === baseIndex ? point.view.value || 0 : 0);
        continue;
      }
      const match = findValue(lookups[index], unixTime);
      if (!match.found) return rows;
      values.push(match.value || 0);
    }
    const localTime = momentTz
      .utc(point.view.groupDate)
      .tz(timeZone)
      .format("MM/DD/YYYY h:mm:ss a");
    rows.push([new Date(localTime), ...values]);
    return rows;
  }, []);
};

const schema = {
  loading: false,
  loadingCsv: false,
  loadingData: false,
  loadingFirmware: false,
  error: false,
  totalElements: 0,
  deviceList: [],
  deviceDetails: {},
  propertySetDetails: {},
  propertyDetails: {},
  property: [],
  propertySet: [],
  firmwareList: [],
  firmwareDetail: {},
  graphData: [],
  graphDataApi: false,
  graphApiCount: 0,
  graphReqApiCount: 0,
  firmwareTotalElements: 0,
  success: false,
  refresh: false,
  loadingGraph: false,
  userSensorPosSaved: false,
  graphArray: {
    hrValue: [
      [{ type: "date", label: "Day" }, "Heart Rate"],
      [new Date(d.getUTCDate()), 0],
    ],
    rrValue: [
      [{ type: "date", label: "Day" }, "Respiratory Rate"],
      [new Date(d.getUTCDate()), 0],
    ],
    stressValue: [
      [{ type: "date", label: "Day" }, "Stress Rate"],
      [new Date(d.getUTCDate()), 0],
    ],
    motionValue: [
      [{ type: "date", label: "Day" }, "Stress Rate"],
      [new Date(d.getUTCDate()), 0],
    ],
    rssHrMvValue: [
      [{ type: "date", label: "Day" }, "Rss Hr Mv Rate"],
      [new Date(d.getUTCDate()), 0],
    ],
    rssRrMvValue: [
      [{ type: "date", label: "Day" }, "Rss Rr Mv Stress Rate"],
      [new Date(d.getUTCDate()), 0],
    ],
  },
  backDateData: false,
  selectedInteval: "",
  swVersion: "",
  hwVersion: "",
  deviceTable: {},
  deviceTableHeader: {},
  friendlyNameData: [],
  friendlyNameUser1Data: [],
  friendlyNameUser2Data: [],
  friendlyNameUser3Data: [],
  friendlyNameUser4Data: [],
  errorLog: {
    count: 0,
    data: [],
  },
  rangeStartDateTime: "",
  rangeEndDateTime: "",
  sensorControl: {},
  updateSensorStatus: false,
  diagnosticPanel1: emptyDiagnosticChart(),
  diagnosticPanel2: emptyDiagnosticChart(),
  diagnosticFilter: {},
  diagnosticFilterLoading: false,
  diagnosticList: [],
  totalDiagnosticElements: 0,
  totalUnreadAlert: 0,
  deviceDataById: {}
};

const reducer = {
  [FETCH_ALERT_LOG_DATA_REQUEST]: (state) => {
    return {
      ...state,
      loading: true,
      errorLog: {
        count: 0,
        data: [],
      },
    };
  },
  [FETCH_ALERT_LOG_DATA_SUCCESS]: (state, { payload }) => {
    return {
      ...state,
      loading: false,
      errorLog: {
        count: payload.totalElements,
        data: payload.content.reduce((acc, val) => {
          let timeProvided = val.data.TimestampA.valueProvidedOn;
          let userTimeZone = {abbr: ""};
          if (
            val.data.TimestampA && 
            val.data.TimestampA.value && 
            val.data.DevidAlerts && 
            val.data.DevidAlerts.value && 
            payload.macIdObj[val.data.DevidAlerts.value]
          ) {
            userTimeZone = timezones.find(
              (time) => time.value === payload.macIdObj[val.data.DevidAlerts.value]
            )
            if (userTimeZone && userTimeZone.utc && userTimeZone.utc.length > 0) {
              timeProvided = momentTz
                .utc(val.data.TimestampA.valueProvidedOn)
                .tz(userTimeZone.utc[0])
                .format("MM/DD/YYYY hh:mm a")
            }
          }

          let obj = {
            residentName: val.owner.fullName,
            DevidAlerts: val.data.DevidAlerts ? val.data.DevidAlerts.value : "",
            FriendlyNameA: val.data.FriendlyNameA
              ? val.data.FriendlyNameA.value
              : "",
            AlertFlag: val.data.AlertFlag ? val.data.AlertFlag.value : "",
            AlertType: val.data.AlertType ? val.data.AlertType.value : "",
            Atext: val.data.Atext ? val.data.Atext.value : "",
            TimestampA: val.data.TimestampA
              ? timeProvided
              : "",
            deviceDataId: val.deviceDataId,
            abbrObj: userTimeZone
          };
          return [...acc, obj];
        }, []),
      },
    };
  },
  [FETCH_ALERT_LOG_DATA_ERROR]: (state) => {
    return {
      ...state,
      loading: false,
    };
  },
  [FETCH_FRIENDLY_NAME_REQUEST]: (state, { payload }) => {
    // Diagnostic Panel slots: drop the old list so a stale device can't be picked
    const userSlotKey = payload?.user
      ? `friendlyNameUser${payload.user.replace("user", "")}Data`
      : null;
    if (userSlotKey) {
      return {
        ...state,
        friendlyNameData: [],
        [userSlotKey]: [],
        [`${userSlotKey}Loading`]: true,
      };
    }
    return {
      ...state,
      loading: true,
      friendlyNameData: [],
    };
  },
  [FETCH_DEVICE_REQUEST]: (state) => {
    return {
      ...state,
      ...REDUCERS.LOADING,
      totalElements: 0,
      deviceList: [],
      deviceDetails: {},
      propertySetDetails: {},
      propertyDetails: {},
      property: [],
      propertySet: [],
      firmwareList: [],
      firmwareDetail: {},
      graphData: [],
      graphDataApi: false,
      graphApiCount: 0,
      graphReqApiCount: 0,
      deviceTable: {},
      deviceTableHeader: {},
      firmwareTotalElements: 0,
      success: false,
    };
  },
  [FETCH_DEVICE_SUCCESS]: (state, { payload }) => {
    return {
      ...state,
      ...REDUCERS.SUCCESS,
      totalElements: payload.totalElements,
      deviceList: payload.content,
    };
  },
  [FETCH_DEVICE_ERROR]: (state) => ({
    ...state,
    ...REDUCERS.ERROR,
  }),
  [FETCH_DEVICE_PROPERTY_REQUEST]: (state) => {
    return {
      ...state,
      ...REDUCERS.LOADING,
    };
  },
  [FETCH_DEVICE_PROPERTY_SUCCESS]: (state, { payload }) => {
    return {
      ...state,
      ...REDUCERS.SUCCESS,
      property: payload,
    };
  },
  [FETCH_DEVICE_PROPERTY_ERROR]: (state) => {
    return {
      ...state,
      ...REDUCERS.ERROR,
    };
  },
  [GET_DEVICE_DETAIL_REQUEST]: (state) => {
    return {
      ...state,
      ...REDUCERS.LOADING,
      totalElements: 0,
      deviceList: [],
      deviceDetails: {},
      propertySetDetails: {},
      propertyDetails: {},
      property: [],
      propertySet: [],
      firmwareList: [],
      firmwareDetail: {},
      firmwareTotalElements: 0,
      success: false,
    };
  },
  [GET_DEVICE_DETAIL_SUCCESS]: (state, { payload }) => {
    payload.device.properties = payload.property.content.map((val) => {
      val.deviceProperties = val.deviceProperties.sort(
        (a, b) => a.displayOrder - b.displayOrder
      );
      let zeroIndexData = val.deviceProperties.filter(
        (a) => a.displayOrder === 0
      );
      let greaterThenZeroIndexData = val.deviceProperties.filter(
        (a) => a.displayOrder > 0
      );
      val.deviceProperties = [...greaterThenZeroIndexData, ...zeroIndexData];
      return val;
    });
    return {
      ...state,
      ...REDUCERS.SUCCESS,
      deviceDetails: payload.device,
      success: false,
    };
  },
  [GET_DEVICE_DETAIL_PROPERTY_ERROR]: (state, { payload }) => {
    payload.device.properties = [];
    return {
      ...state,
      ...REDUCERS.SUCCESS,
      deviceDetails: payload.device,
    };
  },
  [GET_DEVICE_DETAIL_ERROR]: (state) => {
    return {
      ...state,
      ...REDUCERS.ERROR,
    };
  },
  [FETCH_DEVICE_PROPERTY_SET_REQUEST]: (state) => {
    return {
      ...state,
      ...REDUCERS.LOADING,
    };
  },
  [FETCH_DEVICE_PROPERTY_SET_SUCCESS]: (state, { payload }) => {
    return {
      ...state,
      ...REDUCERS.SUCCESS,
      propertySet: payload.content,
    };
  },
  [FETCH_DEVICE_PROPERTY_SET_ERROR]: (state) => ({
    ...state,
    ...REDUCERS.ERROR,
  }),
  [ADD_DEVICE_REQUEST]: (state) => ({
    ...state,
    ...REDUCERS.LOADING,
  }),
  [ADD_DEVICE_ERROR]: (state) => ({
    ...state,
    ...REDUCERS.ERROR,
  }),
  [ADD_DEVICE_SUCCESS]: (state, { payload }) => {
    return {
      ...state,
      ...REDUCERS.SUCCESS,
      deviceDetails: payload,
      success: true,
    };
  },
  [DELETE_DEVICE_REQUEST]: (state) => ({
    ...state,
    ...REDUCERS.LOADING,
  }),
  [DELETE_DEVICE_ERROR]: (state) => ({
    ...state,
    ...REDUCERS.ERROR,
  }),
  [UPDATE_DEVICE_REQUEST]: (state) => ({
    ...state,
    ...REDUCERS.LOADING,
  }),
  [UPDATE_DEVICE_ERROR]: (state) => ({
    ...state,
    ...REDUCERS.ERROR,
  }),
  [UPDATE_DEVICE_SUCCESS]: (state) => {
    return {
      ...state,
      ...REDUCERS.SUCCESS,
    };
  },
  [CREATE_PROPERTY_SET_REQUEST]: (state) => ({
    ...state,
    ...REDUCERS.LOADING,
  }),
  [CREATE_PROPERTY_SET_ERROR]: (state) => ({
    ...state,
    ...REDUCERS.ERROR,
  }),
  [CREATE_PROPERTY_SET_SUCCESS]: (state) => ({
    ...state,
    ...REDUCERS.SUCCESS,
    success: true,
  }),
  [DELETE_PROPERTY_SET_REQUEST]: (state) => ({
    ...state,
    ...REDUCERS.LOADING,
  }),
  [DELETE_PROPERTY_SET_ERROR]: (state) => ({
    ...state,
    ...REDUCERS.ERROR,
  }),
  [RESET_SUCCESS]: (state) => {
    return {
      ...state,
      refresh: false,
      success: false,
      diagnosticPanel1: emptyDiagnosticChart(),
      diagnosticPanel2: emptyDiagnosticChart(),
    };
  },
  [FETCH_PROPERTY_SET_DETAIL_REQUEST]: (state) => ({
    ...state,
    ...REDUCERS.LOADING,
  }),
  [FETCH_PROPERTY_SET_DETAIL_ERROR]: (state) => ({
    ...state,
    ...REDUCERS.ERROR,
  }),
  [FETCH_PROPERTY_SET_DETAIL_SUCCESS]: (state, { payload }) => {
    return {
      ...state,
      ...REDUCERS.SUCCESS,
      propertySetDetails: payload,
    };
  },
  [UPDATE_PROPERTY_SET_REQUEST]: (state) => ({
    ...state,
    ...REDUCERS.LOADING,
  }),
  [UPDATE_PROPERTY_SET_SUCCESS]: (state) => ({
    ...state,
    ...REDUCERS.SUCCESS,
    success: true,
  }),
  [UPDATE_PROPERTY_SET_ERROR]: (state) => ({
    ...state,
    ...REDUCERS.ERROR,
  }),
  [GET_DEVICE_PROPERTY_REQUEST]: (state) => ({
    ...state,
    ...REDUCERS.LOADING,
  }),
  [GET_DEVICE_PROPERTY_SUCCESS]: (state, { payload }) => {
    return {
      ...state,
      ...REDUCERS.SUCCESS,
      propertyDetails: payload,
    };
  },
  [GET_DEVICE_PROPERTY_ERROR]: (state) => ({
    ...state,
    ...REDUCERS.ERROR,
  }),
  [UPDATE_DEVICE_PROPERTY_SUCCESS]: (state) => ({
    ...state,
    ...REDUCERS.SUCCESS,
    success: true,
  }),
  [UPDATE_DEVICE_PROPERTY_REQUEST]: (state) => ({
    ...state,
    ...REDUCERS.LOADING,
  }),
  [UPDATE_DEVICE_PROPERTY_ERROR]: (state) => ({
    ...state,
    ...REDUCERS.ERROR,
  }),
  [CREATE_DEVICE_PROPERTY_SUCCESS]: (state) => ({
    ...state,
    ...REDUCERS.SUCCESS,
    success: true,
  }),
  [CREATE_DEVICE_PROPERTY_REQUEST]: (state) => ({
    ...state,
    ...REDUCERS.LOADING,
  }),
  [CREATE_DEVICE_PROPERTY_ERROR]: (state) => ({
    ...state,
    ...REDUCERS.ERROR,
  }),
  [DELETE_PROPERTY_REQUEST]: (state) => ({
    ...state,
    ...REDUCERS.LOADING,
  }),
  [DELETE_PROPERTY_ERROR]: (state) => ({
    ...state,
    ...REDUCERS.ERROR,
  }),
  [FETCH_DEVICE_FIRMWARE_REQUEST]: (state) => ({
    ...state,
    ...REDUCERS.LOADING,
    success: false,
  }),
  [FETCH_DEVICE_FIRMWARE_ERROR]: (state) => ({
    ...state,
    ...REDUCERS.ERROR,
    firmwareList: [],
    firmwareDetail: {},
    firmwareTotalElements: 0,
  }),
  [FETCH_DEVICE_FIRMWARE_SUCCESS]: (state, { payload }) => {
    return {
      ...state,
      ...REDUCERS.SUCCESS,
      firmwareList: payload.content,
      firmwareTotalElements: payload.totalElements,
      firmwareDetail: {},
    };
  },
  [FETCH_FIRMWARE_DETAIL_REQUEST]: (state) => ({
    ...state,
    ...REDUCERS.LOADING,
    success: false,
  }),
  [FETCH_FIRMWARE_DETAIL_ERROR]: (state) => ({
    ...state,
    ...REDUCERS.ERROR,
    success: false,
  }),
  [FETCH_FIRMWARE_DETAIL_SUCCESS]: (state, { payload }) => {
    return {
      ...state,
      ...REDUCERS.SUCCESS,
      firmwareDetail: payload,
    };
  },
  [UPDATE_FIRMWARE_DETAIL_REQUEST]: (state) => ({
    ...state,
    ...REDUCERS.LOADING,
  }),
  [UPDATE_FIRMWARE_DETAIL_ERROR]: (state) => ({
    ...state,
    ...REDUCERS.ERROR,
  }),
  [UPDATE_FIRMWARE_DETAIL_SUCCESS]: (state) => ({
    ...state,
    ...REDUCERS.SUCCESS,
    success: true,
  }),
  [CREATE_FIRMWARE_DETAIL_REQUEST]: (state) => ({
    ...state,
    ...REDUCERS.LOADING,
  }),
  [CREATE_FIRMWARE_DETAIL_ERROR]: (state) => ({
    ...state,
    ...REDUCERS.ERROR,
  }),
  [CREATE_FIRMWARE_DETAIL_SUCCESS]: (state) => ({
    ...state,
    ...REDUCERS.SUCCESS,
    success: true,
  }),
  [DUPLICATE_DEVICE_REQUEST]: (state) => ({
    ...state,
    ...REDUCERS.LOADING,
  }),
  [DUPLICATE_DEVICE_ERROR]: (state) => ({
    ...state,
    ...REDUCERS.ERROR,
  }),
  [DUPLICATE_DEVICE_SUCCESS]: (state) => ({
    ...state,
    ...REDUCERS.SUCCESS,
    success: true,
  }),
  [RESET_GRAPH_SUCCESS]: (state) => {
    return {
      ...state,
      graphArray: {
        hrValue: [
          [{ type: "date", label: "Day" }, "Heart Rate"],
          [new Date(d.getUTCDate()), 0],
        ],
        rrValue: [
          [{ type: "date", label: "Day" }, "Respiratory Rate"],
          [new Date(d.getUTCDate()), 0],
        ],
        stressValue: [
          [{ type: "date", label: "Day" }, "Stress Rate"],
          [new Date(d.getUTCDate()), 0],
        ],
        motionValue: [
          [{ type: "date", label: "Day" }, "Motion Rate"],
          [new Date(d.getUTCDate()), 0],
        ],
        rssHrMvValue: [
          [{ type: "date", label: "Day" }, "Rss Hr Mv Rate"],
          [new Date(d.getUTCDate()), 0],
        ],
        rssRrMvValue: [
          [{ type: "date", label: "Day" }, "Rss Rr Mv Stress Rate"],
          [new Date(d.getUTCDate()), 0],
        ],
        sdnnValue: [
          [{ type: "date", label: "Day" }, "Sdnn Rate"],
          [new Date(d.getUTCDate()), 0],
        ],
        rmssdValue: [
          [{ type: "date", label: "Day" }, "Rmssd Rate"],
          [new Date(d.getUTCDate()), 0],
        ],
        hrTdValue: [
          [{ type: "date", label: "Day" }, "Hr Td Rate"],
          [new Date(d.getUTCDate()), 0],
        ],
        hrFdValue: [
          [{ type: "date", label: "Day" }, "Hr Fd Rate"],
          [new Date(d.getUTCDate()), 0],
        ],
        hrSelValue: [
          [{ type: "date", label: "Day" }, "Hr Sel Rate"],
          [new Date(d.getUTCDate()), 0],
        ],
        hrEstValue: [
          [{ type: "date", label: "Day" }, "Hr Est Rate"],
          [new Date(d.getUTCDate()), 0],
        ],
        winFdValue: [
          [{ type: "date", label: "Day" }, "Win Fd Rate"],
          [new Date(d.getUTCDate()), 0],
        ],
        winSelValue: [
          [{ type: "date", label: "Day" }, "Win Sel Rate"],
          [new Date(d.getUTCDate()), 0],
        ],
        winEstValue: [
          [{ type: "date", label: "Day" }, "Win Est Rate"],
          [new Date(d.getUTCDate()), 0],
        ],
      },
      backDateData: false,
    };
  },
  [FETCH_DEVICE_SPECIFIED_REQUEST]: (state) => ({
    ...state,
    ...REDUCERS.LOADING,
    deviceTableHeader: {},
    deviceTable: {},
  }),
  [FETCH_DEVICE_SPECIFIED_SUCCESS]: (state, { payload }) => {
    return {
      ...state,
      ...REDUCERS.SUCCESS,
      deviceTableHeader: payload,
    };
  },
  [FETCH_DEVICE_SPECIFIED_ERROR]: (state) => ({
    ...state,
    ...REDUCERS.ERROR,
    deviceTableHeader: {},
    deviceTable: {},
  }),
  [GET_PROPERTY_DATA_REQUEST]: (state) => ({
    ...state,
    ...REDUCERS.LOADING,
  }),
  [GET_PROPERTY_DATA_SUCCESS]: (state, { payload }) => {
    return {
      ...state,
      ...REDUCERS.SUCCESS,
      deviceTable: sortDeviceTable({ ...state.deviceTable, ...payload }),
    };
  },
  [FETCH_STAT_SAMPLES_REQUEST]: (state) => ({
    ...state,
    ...REDUCERS.LOADING,
  }),
  [FETCH_STAT_SAMPLES_SUCCESS]: (state, { payload }) => {
    const { type, devicePropertyCodes, dataTypes, data } = payload;
    return {
      ...state,
      ...REDUCERS.SUCCESS,
      deviceTableHeader: {
        ...state.deviceTableHeader,
        [type]: { ...state.deviceTableHeader[type], devicePropertyCodes, dataTypes },
      },
      deviceTable: sortDeviceTable({
        ...state.deviceTable,
        [type]: { data, totalElements: data.length },
      }),
    };
  },
  [FETCH_STAT_SAMPLES_ERROR]: (state, { payload }) => ({
    ...state,
    ...REDUCERS.SUCCESS,
    deviceTable: sortDeviceTable({
      ...state.deviceTable,
      [payload.type]: { data: [], totalElements: 0 },
    }),
  }),
  [CREATE_DEVICE_DATA_REQUEST]: (state) => ({
    ...state,
    ...REDUCERS.LOADING,
  }),
  [CREATE_DEVICE_DATA_SUCCESS]: (state) => ({
    ...state,
    ...REDUCERS.SUCCESS,
  }),
  [CREATE_DEVICE_DATA_ERROR]: (state) => ({
    ...state,
    ...REDUCERS.ERROR,
  }),
  [UPDATE_DEVICE_DATA_REQUEST]: (state) => ({
    ...state,
    ...REDUCERS.SUCCESS,
  }),
  [UPDATE_DEVICE_DATA_ERROR]: (state) => ({
    ...state,
    ...REDUCERS.ERROR,
  }),
  [UPDATE_DEFAULT_DATA_REQUEST]: (state) => ({
    ...state,
    ...REDUCERS.LOADING,
  }),
  [UPDATE_DEFAULT_DATA_ERROR]: (state) => ({
    ...state,
    ...REDUCERS.ERROR,
  }),
  [UPDATE_DEFAULT_THRESHOLD_REQUEST]: (state) => ({
    ...state,
    ...REDUCERS.LOADING,
  }),
  [UPDATE_DEFAULT_THRESHOLD_ERROR]: (state) => ({
    ...state,
    ...REDUCERS.ERROR,
  }),
  [DELETE_DEVICE_DATA_REQUEST]: (state) => ({
    ...state,
    ...REDUCERS.LOADING,
  }),
  [DELETE_DEVICE_DATA_ERROR]: (state) => ({
    ...state,
    ...REDUCERS.ERROR,
  }),
  [SYNC_FRIENDLY_NAME_REQUEST]: (state) => ({
    ...state,
    ...REDUCERS.LOADING,
  }),
  [SYNC_FRIENDLY_NAME_ERROR]: (state) => ({
    ...state,
    ...REDUCERS.ERROR,
  }),
  [FETCH_FRIENDLY_NAME_SUCCESS]: (state, { payload }) => {
    const byFriendlyName = (a, b) => (a.friendlyName > b.friendlyName ? 1 : -1);
    const friendlyNameObj = payload.user
      ? [...payload.friendlyNameData].sort(byFriendlyName)
      : [
        {
          macId: "all",
          friendlyName: "Select All",
        },
        ...payload.friendlyNameData.sort(byFriendlyName),
      ];
    // payload.user is "user1".."user4" when called from the Diagnostic Panel
    const userSlotKey = payload.user
      ? `friendlyNameUser${payload.user.replace("user", "")}Data`
      : null;
    return {
      ...state,
      ...REDUCERS.SUCCESS,
      loading: false,
      ...(userSlotKey ? { [userSlotKey]: friendlyNameObj, [`${userSlotKey}Loading`]: false } : {}),
      friendlyNameData: friendlyNameObj,
    };
  },
  [FETCH_FRIENDLY_NAME_ERROR]: (state, { payload }) => {
    const userSlotKey = payload?.user
      ? `friendlyNameUser${payload.user.replace("user", "")}Data`
      : null;
    return {
      ...state,
      ...REDUCERS.ERROR,
      loading: false,
      friendlyNameData: [],
      ...(userSlotKey ? { [userSlotKey]: [], [`${userSlotKey}Loading`]: false } : {}),
    };
  },
  [DOWNLOAD_DEVICE_FIRMWARE_REQUEST]: (state) => {
    return {
      ...state,
      loadingFirmware: true,
    };
  },
  [DOWNLOAD_DEVICE_FIRMWARE_SUCCESS]: (state) => {
    return {
      ...state,
      loadingFirmware: false,
    };
  },
  [DOWNLOAD_DEVICE_FIRMWARE_ERROR]: (state) => {
    return {
      ...state,
      loadingFirmware: false,
    };
  },
  [FETCH_DIAGNOSTIC_PANEL_DATA_REQUEST]: (state) => {
    return {
      ...state,
      loadingData: true,
      diagnosticPanel1: emptyDiagnosticChart(),
      diagnosticPanel2: emptyDiagnosticChart(),
    };
  },
  [FETCH_DIAGNOSTIC_PANEL_DATA_ERROR]: (state) => {
    return {
      ...state,
      loadingData: false,
      diagnosticPanel1: emptyDiagnosticChart(),
      diagnosticPanel2: emptyDiagnosticChart(),
    };
  },
  [FETCH_DIAGNOSTIC_PANEL_DATA_SUCCESS]: (state, { payload }) => {
    const headings = ["Time", ...payload.labels];
    return {
      ...state,
      loadingData: false,
      diagnosticPanel1: [
        headings,
        ...buildDiagnosticRows(payload.firstGraphData, payload.currentUserTimeZone),
      ],
      diagnosticPanel2: [
        headings,
        ...buildDiagnosticRows(payload.secondGraphData, payload.currentUserTimeZone),
      ],
    };
  },
  [RESET_DIAGNOSTIC_PANEL_DATA_REQUEST]: (state) => {
    return {
      ...state,
      loadingData: false,
      diagnosticPanel1: emptyDiagnosticChart(),
      diagnosticPanel2: emptyDiagnosticChart(),
    };
  },
  [REMOVE_DIAGNOSTIC_USER_SLOT]: (state, { payload }) => {
    // Shift friendly-name lists up so they stay aligned with the remaining user slots
    const slots = [1, 2, 3, 4].map((slot) => ({
      list: state[`friendlyNameUser${slot}Data`],
      loading: state[`friendlyNameUser${slot}DataLoading`],
    }));
    slots.splice(payload - 1, 1);
    slots.push({ list: [], loading: false });
    return slots.reduce(
      (acc, { list, loading }, index) => ({
        ...acc,
        [`friendlyNameUser${index + 1}Data`]: list,
        [`friendlyNameUser${index + 1}DataLoading`]: loading,
      }),
      { ...state }
    );
  },
  [SAVE_DIAGNOSTIC_PANEL_DATA_REQUEST]: (state) => {
    return {
      ...state,
      loadingData: true,
    };
  },
  [SAVE_DIAGNOSTIC_PANEL_DATA_ERROR]: (state) => {
    return {
      ...state,
      loadingData: false,
    };
  },
  [SAVE_DIAGNOSTIC_PANEL_DATA_SUCCESS]: (state, { payload }) => {
    return {
      ...state,
      loadingData: false,
    };
  },
  [FETCH_SAVED_DIAGNOSTIC_PANEL_DATA_REQUEST]: (state) => {
    return {
      ...state,
      diagnosticFilterLoading: true,
      diagnosticFilter: {},
    };
  },
  [FETCH_SAVED_DIAGNOSTIC_PANEL_DATA_ERROR]: (state) => {
    return {
      ...state,
      diagnosticFilterLoading: false,
      diagnosticFilter: {},
    };
  },
  [FETCH_SAVED_DIAGNOSTIC_PANEL_DATA_SUCCESS]: (state, { payload }) => {
    let intervalStartTime = momentTz
      .tz(DEFAULT_UTC_TIME)
      .subtract("1", "h")
      .format("MM/DD/YYYY h:mm a");
    if (payload?.[0]?.data?.Interval?.value) {
      const intervalType = payload[0].data?.Interval?.value.split(" ")[1]
        ? payload[0].data?.Interval?.value.split(" ")[1].toLowerCase()
        : payload[0].data?.Interval?.value.split(" ")[0].toLowerCase();

      if (intervalType === "hr") {
        intervalStartTime = momentTz
          .tz(DEFAULT_UTC_TIME)
          .subtract(payload[0].data?.Interval?.value.split(" ")[0], "h")
          .format("MM/DD/YYYY h:mm a");
      } else if (intervalType === "min" || intervalType === "mins") {
        intervalStartTime = momentTz
          .tz(DEFAULT_UTC_TIME)
          .subtract(payload[0].data?.Interval?.value.split(" ")[0], "m")
          .format("MM/DD/YYYY h:mm a");
      } else if (intervalType === "daily") {
        intervalStartTime = momentTz
          .tz(DEFAULT_UTC_TIME)
          .subtract(24, "h")
          .format("MM/DD/YYYY h:mm a");
      } else if (intervalType === "weekly") {
        intervalStartTime = momentTz
          .tz(DEFAULT_UTC_TIME)
          .subtract(7, "day")
          .format("MM/DD/YYYY h:mm a");
      } else if (intervalType === "monthly") {
        intervalStartTime = momentTz
          .tz(DEFAULT_UTC_TIME)
          .subtract(30, "day")
          .format("MM/DD/YYYY h:mm a");
      }
    }
    return {
      ...state,
      diagnosticFilterLoading: false,
      diagnosticFilter: {
        deviceDataId: payload?.[0]?.deviceDataId || "",
        userId1: payload?.[0]?.data?.User1?.value || "",
        userId2: payload?.[0]?.data?.User2?.value || "",
        userId3: payload?.[0]?.data?.User3?.value || "",
        macAddress1: payload?.[0]?.data?.Fname1?.value || "",
        macAddress2: payload?.[0]?.data?.Fname2?.value || "",
        macAddress3: payload?.[0]?.data?.Fname3?.value || "",
        interval: payload?.[0]?.data?.Interval?.value || "",
        graphProperty1: payload?.[0]?.data?.Graph1?.value || "",
        graphProperty2: payload?.[0]?.data?.Graph2?.value || "",
        timezone: payload?.[0]?.data?.Timezone?.value || "",
        intervalStartTime,
        // intervalStartTime: payload[0].data?.Starttime?.value
        //   ? moment(payload[0].data?.Starttime?.value).format(
        //       "MM/DD/YYYY h:mm a"
        //     )
        //   : "",
        loggedUserId: payload?.[0]?.data?.Createdbyuser?.value || "",
        timeformat: payload?.[0]?.data?.Timeformat?.value || "",
        // 4th user slot only exists on newer saved filters
        ...(payload?.[0]?.data?.User4
          ? {
            userId4: payload[0].data.User4.value || "",
            macAddress4: payload[0].data?.Fname4?.value || "",
          }
          : {}),
      },
    };
  },
  [FETCH_DIAGNOSTIC_PANEL_REQUEST]: (state) => {
    return {
      ...state,
      loadingData: true,
      diagnosticList: [],
    };
  },
  [FETCH_DIAGNOSTIC_PANEL_ERROR]: (state) => {
    return {
      ...state,
      loadingData: false,
      diagnosticList: [],
    };
  },
  [FETCH_DIAGNOSTIC_PANEL_SUCCESS]: (state, { payload }) => {
    return {
      ...state,
      loadingData: false,
      diagnosticList: payload.content,
      totalDiagnosticElements: payload.totalElements,
    };
  },
  [SAVE_USER_ORDER_REQUEST]: (state) => ({
    ...state,
    ...REDUCERS.LOADING,
    userSensorPosSaved: false
  }),
  [SAVE_USER_ORDER_SUCCESS]: (state) => ({
    ...state,
    ...REDUCERS.SUCCESS,
    userSensorPosSaved: true
  }),
  [SAVE_USER_ORDER_ERROR]: (state) => ({
    ...state,
    ...REDUCERS.ERROR,
    userSensorPosSaved: false
  }),
  [FETCH_ERROR_LOG_CSV_SUCCESS]: state => ({
    ...state,
    loadingData: false,
    success: true
  }),
  [FETCH_ERROR_LOG_CSV_ERROR]: state => ({
    ...state,
    loadingData: false,
    success: false
  }),
  [FETCH_ALERT_LOG_CSV_REQUEST]: state => ({
    ...state,
    loadingData: true,
    success: false
  }),
  [FETCH_ALERT_LOG_CSV_SUCCESS]: state => ({
    ...state,
    loadingData: false,
    success: true
  }),
  [FETCH_ALERT_LOG_CSV_ERROR]: state => ({
    ...state,
    loadingData: false,
    success: false
  }),
};

export default createReducer(schema, reducer);
